import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import FileUpload from '../../components/FileUpload';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { uploadPromoAsync, deletePromoFile } from '../../services/trackService';
import type { ImageFile } from '../../services/trackTypes';
import { FaLink } from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import { logError, logInfo } from '../../services/logtail';
import { useWebSocketNotifications } from '../../hooks/useWebSocketNotifications';
import type { ImportNotification, UploadNotification } from '../../types/notifications';
import { resolveTrackMediaUrl } from '../../services/mediaAccess';
import CassetteLoader from '../../components/CassetteLoader';
import ImageLightbox from '../../components/ImageLightbox';
import { SketchProgressBar } from '../../components/sketchy';
import type { TrackPermissions } from '../../types/track';
import BinaryUnavailablePlaceholder from '../../components/BinaryUnavailablePlaceholder';
import type { BinaryInactiveReason } from '../../components/BinaryUnavailablePlaceholder';

interface PromoImagesSectionProps {
  promoImages: ImageFile[];
  setPromoImages: Dispatch<SetStateAction<ImageFile[]>>;
  importFromLastTrack: () => void;
  cloudFolderUrl?: string;
  trackId: string;
  importFileCount?: number;
  permissions?: TrackPermissions;
  isOwner?: boolean;
  binaryAccess?: boolean;
  binaryInactiveReason?: BinaryInactiveReason;
  requiredStorageProvider?: string;
  viewerRole?: 'owner' | 'collaborator';
  storageProvider?: string;
}

const PromoImagesSection: React.FC<PromoImagesSectionProps> = ({
  promoImages,
  setPromoImages,
  importFromLastTrack,
  cloudFolderUrl,
  trackId,
  importFileCount = 0,
  permissions,
  isOwner: _isOwner = true,
  binaryAccess,
  binaryInactiveReason,
  requiredStorageProvider,
  viewerRole = 'collaborator',
  storageProvider,
}) => {
  const { t } = useTranslation();
  const { user, unlinkGoogleDrive } = useAuth();

  // Upload states
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [filesRemaining, setFilesRemaining] = useState(0);
  const [pendingPromoFiles, setPendingPromoFiles] = useState<File[]>([]);

  // Image preview states
  const [imageBlobs, setImageBlobs] = useState<Record<string, string>>({});
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({});
  const [previewImg, setPreviewImg] = useState<string | null>(null);
  const blobUrlsRef = useRef<string[]>([]);

  // Use centralized WebSocket notifications hook (filter strictly to promo image uploads)
  useWebSocketNotifications({
    trackId: trackId,
    fileType: 'promo',
    onAuthIssue: (notification) => {
      // Handle Google Drive auth issues - the hook will automatically trigger re-auth
      logInfo('🔐 AUTH ISSUE: PromoImagesSection received auth issue notification', { 
        notification,
        trackId: trackId,
        component: 'PromoImagesSection'
      });
    },
    onImportUpdate: (notification: ImportNotification) => {
      if (notification.type === 'import_started') {
        // Individual image import started - initialize the state properly
        setIsUploading(true);
        setIsImporting(true);
        // Set the initial count of files to process
        if (importFileCount > 0) {
          setFilesRemaining(importFileCount);
        }
      } else if (notification.type === 'import_completed') {
        // Individual image import completed - decrement the count
        setFilesRemaining(prev => {
          const newCount = Math.max(0, prev - 1);
          
          // Show success toast for this individual image with the correct remaining count
          toast.success(`Image imported successfully! ${newCount} remaining`);
          
          // If all images are processed, schedule state updates for next tick
          if (newCount === 0) {
            setTimeout(() => {
              setIsUploading(false);
              setIsImporting(false);
              setPendingPromoFiles([]); // Clear any pending files
              
              // Refresh promo images from server after import completes
              refreshPromoImages();
            }, 0);
          }
          
          return newCount;
        });
      } else if (notification.type === 'import_failed') {
        setIsUploading(false);
        setIsImporting(false);
        setFilesRemaining(0);
        setPendingPromoFiles([]); // Clear any pending files
        toast.error('Import failed: ' + (notification.error || 'Unknown error'));
      }
    },
    onUploadComplete: (notification: UploadNotification) => {
      setFilesRemaining(prev => {
        const newCount = Math.max(0, prev - 1);
        // If we've processed all files, schedule state updates for next tick
        if (newCount === 0) {
          setTimeout(() => {
            setIsUploading(false);
            setPendingPromoFiles([]); // Clear the file preview section
          }, 0);
        }
        return newCount;
      });
      
      // Add the new image to the promoImages state (guard by type)
      if (notification.file_type === 'promo' && notification.file_id) {
        const newImage: ImageFile = {
          fileId: notification.file_id,
          name: notification.filename || 'Unknown',
          url: '' // Will be loaded by loadImagePreview
        };
        setPromoImages(prev => [...prev, newImage]);
      }
    }
  });

  // Handle file upload
  const handleFileUpload = useCallback((files: FileList | File[]) => {
    const arr = Array.isArray(files) ? files : Array.from(files);
    setPendingPromoFiles(prev => [...prev, ...arr]);
  }, []);

  // Handle bulk upload
  const handleBulkUpload = useCallback(async () => {
    setIsUploading(true);
    setFilesRemaining(pendingPromoFiles.length);
    try {
      if (!trackId) return;
      
      for (const file of pendingPromoFiles) {
        await uploadPromoAsync(trackId, file);
      }
    } catch (error) {
      logError('Promo upload failed', error as Error);
      toast.error('Upload failed. Please try again.');
      setIsUploading(false);
      setFilesRemaining(0);
    }
  }, [pendingPromoFiles, trackId]);

  // Load image previews
  const loadImagePreview = useCallback(async (fileId: string) => {
    // Avoid duplicate fetches by checking current state best-effort; not critical if duplicated
    if (imageBlobs[fileId] || loadingImages[fileId]) return;
    
    setLoadingImages(prev => ({ ...prev, [fileId]: true }));
    try {
      const access = await resolveTrackMediaUrl(trackId, fileId, 'promo', storageProvider);
      if (access.revoke) {
        blobUrlsRef.current.push(access.url);
      }
      setImageBlobs(prev => ({ ...prev, [fileId]: access.url }));
    } catch {
      setImageBlobs(prev => ({ ...prev, [fileId]: 'placeholder' }));
    } finally {
      setLoadingImages(prev => ({ ...prev, [fileId]: false }));
    }
  }, [trackId, storageProvider]);

  // Load all image previews (no cleanup here; handled on unmount)
  useEffect(() => {
    promoImages.forEach(img => {
      const fileId = img.fileId || img.name;
      loadImagePreview(fileId);
    });
  }, [promoImages, loadImagePreview]);

  // Handle import button click
  const handleImportClick = useCallback(() => {
    importFromLastTrack();
  }, [importFromLastTrack]);

  // Refresh promo images from server
  const refreshPromoImages = useCallback(async () => {
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error('No user');
      const token = await user.getIdToken();
      
      const res = await fetch(`${API_CONFIG.BASE_URL}/tracks/${trackId}`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) throw new Error('Failed to fetch track data');
      const data = await res.json();
      
      if (data.promoImages) {
        setPromoImages(data.promoImages);
        logInfo('Refreshed promo images after import', { 
          count: data.promoImages.length,
          trackId 
        });
      }
    } catch (error) {
      logError('Failed to refresh promo images after import', error as Error);
    }
  }, [trackId]);

  // Handle file deletion
  const handleDeleteFile = useCallback(async (index: number) => {
    if (!window.confirm(t('promo.confirmRemovePromoImage'))) return;
    
    try {
      const img = promoImages[index];
      await deletePromoFile(trackId, img.fileId);
      setPromoImages(prev => prev.filter((_, i) => i !== index));
      toast.success(t('promo.removeSuccess'));
    } catch (e) {
      console.error('Error removing promo image:', e);
      toast.error(t('promo.removeError'));
    }
  }, [trackId, promoImages, setPromoImages, t]);

  // Handle Google Drive unlinking
  const handleUnlinkGoogleDrive = async () => {
    try {
      await unlinkGoogleDrive();
      toast.success('Google Drive unlinked successfully');
    } catch (error) {
      logError('Failed to unlink Google Drive', error as Error);
      toast.error('Failed to unlink Google Drive');
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  if (binaryAccess === false) {
    return (
      <BinaryUnavailablePlaceholder
        reason={binaryInactiveReason}
        requiredProvider={requiredStorageProvider}
        viewerRole={viewerRole}
      />
    );
  }

  return (
    <div>
      <div className="relative w-full mb-4">
        {cloudFolderUrl && (
          <a
            href={cloudFolderUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-2 right-2 hover:text-blue-600 z-10"
            style={{ color: 'var(--color-muted)', lineHeight: 0 }}
            title={t('track.openCloudFolder')}
          >
            <FaLink size={24} />
          </a>
        )}
        
        {permissions?.canUploadPromo ? (
          <FileUpload
            label={promoImages.length ? t('promo.addMorePhotos') : t('promo.uploadPhotos')}
            fileCategory="image"
            onFile={handleFileUpload}
            multiple={true}
            showPreview={false}
            data-testid="promo-images-file-input"
          />
        ) : (
          <div className="border-2 border-dashed rounded-lg p-6 text-center opacity-50" style={{ borderColor: 'var(--color-border)' }}>
            <div>
              <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                Upload not available - View only access
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--color-muted)' }}>
                You can view existing promo images below
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Selected Files Preview */}
      {pendingPromoFiles.length > 0 && permissions?.canUploadPromo && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-blue-900">
              {pendingPromoFiles.length} file{pendingPromoFiles.length === 1 ? '' : 's'} selected
            </h4>
            <button
              onClick={() => setPendingPromoFiles([])}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Clear
            </button>
          </div>
          <div className="space-y-2">
            {pendingPromoFiles.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-blue-800 truncate">{file.name}</span>
                <span className="text-blue-600">
                  {file.size < 1024 * 1024 
                    ? `${(file.size / 1024).toFixed(1)} KB`
                    : `${(file.size / 1024 / 1024).toFixed(1)} MB`
                  }
                </span>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={handleBulkUpload}
            disabled={isUploading || isImporting}
            className="mt-3 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="promo-upload-button"
          >
            {isUploading ? 'Uploading...' : `Upload ${pendingPromoFiles.length} File${pendingPromoFiles.length === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {/* Import from Another Track */}
      {permissions?.canUploadPromo && (
        <div className="mt-4">
          <button
            onClick={handleImportClick}
            disabled={isUploading || isImporting}
            className="w-full text-white py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed border border-blue-600 hover:border-blue-500 font-medium transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            style={{
              background: 'var(--brand-gradient-r)'
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.background = 'var(--brand-gradient-r-hover)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--brand-gradient-r)';
            }}
          >
            {t('promo.importFromTrack')}
          </button>
        </div>
      )}

      {/* Upload/Import Status */}
      {(isUploading || isImporting) && filesRemaining > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <CassetteLoader size={48} variant="inline" />
              <span className="text-sm text-yellow-800 font-medium">
                {isImporting ? 'Importing from Google Drive...' : 'Uploading images...'}
              </span>
            </div>
            <span className="text-sm text-yellow-600">{filesRemaining} remaining</span>
          </div>
          <SketchProgressBar
            value={importFileCount > 0 ? ((importFileCount - filesRemaining) / importFileCount) * 100 : 0}
            data-testid="promo-images-import-progress"
          />
        </div>
      )}

      {/* Image Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 mt-4 mb-4 w-full">
        {promoImages.map((img, idx) => {
          const fileId = img.fileId || img.name;
          const isLoading = loadingImages[fileId];
          const blobUrl = imageBlobs[fileId];
          
          return (
          <div
            key={fileId}
            className="rounded-lg border p-2 flex flex-col items-center relative group"
            style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}
            data-testid="promo-image-tile"
          >
              <a href={blobUrl || '#'} target="_blank" rel="noopener noreferrer" onClick={e => { if (!blobUrl) e.preventDefault(); }}>
                {isLoading && (
                  <div className="w-20 h-20 flex items-center justify-center">
                    <CassetteLoader size={48} variant="inline" />
                  </div>
                )}
                
                {!isLoading && blobUrl && blobUrl !== 'placeholder' && (
                  <img
                    src={blobUrl}
                    alt={`Promo ${idx + 1}`}
                    className="w-full aspect-square object-cover rounded shadow cursor-pointer hover:scale-105 transition-transform border"
                    style={{ borderColor: 'var(--color-border)' }}
                    onClick={e => { e.preventDefault(); setPreviewImg(blobUrl); }}
                  />
                )}
                
                {!isLoading && blobUrl === 'placeholder' && (
                  <div className="w-full aspect-square gradient-brand-br rounded border shadow flex items-center justify-center" style={{ borderColor: 'var(--color-border)' }}>
                    <CheckCircle2 className="h-8 w-8 text-white" />
                  </div>
                )}
              </a>
              
              <div className="text-xs font-medium truncate w-full text-center mt-2 mb-1 px-1" style={{ color: 'var(--color-text)' }} title={img.name}>
                {img.name}
              </div>
              
              {permissions?.canUploadPromo && (
                <button
                  type="button"
                  className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded-full transition"
                  style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', color: '#ef4444' }}
                  title={t('promo.removePromoImage')}
                  onClick={() => handleDeleteFile(idx)}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Image Preview Modal */}
      {previewImg && (
        <ImageLightbox src={previewImg} alt="Preview" onClose={() => setPreviewImg(null)} />
      )}
    </div>
  );
};

export default PromoImagesSection;